import React, { useState } from 'react';
import P, { InferProps } from 'prop-types';
import ReactQuill, { DeltaStatic, EmitterSource } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { SxStyle } from '../../../types';
import { Box } from '@mui/material';
import { Article } from '../../scenes/faq/types';

const ArticleHtmlKey: keyof Article = 'html';
const ArticleContentKey: keyof Article = 'content';

const styles: SxStyle = {
  container: {
    position: 'relative',
    '.ql-toolbar': {
      borderRadius: '4px 4px 0 0',
    },
    '.ql-container': {
      borderRadius: '0 0 4px 4px',
    },
    '.ql-editor': {
      minHeight: 200,
    },
    label: (theme) => ({
      fontSize: '1rem',
      fontFamily: theme.typography.fontFamily,
      color: theme.palette.grey[600],
      transform: 'translate(4px, -12px) scale(0.75)',
      position: 'absolute',
      display: 'block',
      top: 0,
      left: 0,
      backgroundColor: 'white',
      padding: '0 6px',
    }),
  },
};

const modules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'], // toggled buttons
    ['blockquote' /*, 'code-block'*/],
    ['link' /* 'image'  'video', 'formula'*/],

    [{ list: 'ordered' }, { list: 'bullet' } /*, { list: 'check' }*/],
    // [{ script: 'sub' }, { script: 'super' }], // superscript/subscript
    // [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
    // [{ direction: 'rtl' }], // text direction

    // [{ size: ['small', false, 'large', 'huge'] }], // custom dropdown

    // [{ color: [] }, { background: [] }], // dropdown with defaults from theme
    // [{ font: [] }],
    // [{ align: [] }],

    ['clean'], // remove formatting button
  ],
};

function RichTextHtmlField({
  setChange,
  field,
  isReadOnly,
  label,
  helperText,
}: InferProps<typeof RichTextHtmlField.propTypes>) {
  const [boxRef, setBoxRef] = useState<HTMLDivElement | null>(null);

  const onChangeHandler = (
    content: string,
    delta: DeltaStatic,
    source: EmitterSource,
    editor: ReactQuill.UnprivilegedEditor
  ) => {
    if (source === 'user') {
      const html = editor.getSemanticHTML();
      const content = editor.getContents();
      setChange(ArticleHtmlKey, html);
      setChange(ArticleContentKey, JSON.stringify(content));
    }
  };

  return (
    <Box sx={styles.container} ref={(ref: HTMLDivElement) => setBoxRef(ref)}>
      <label>{label || field.title}</label>
      {boxRef && (
        <ReactQuill
          theme="snow"
          defaultValue={field.value ? JSON.parse(field.value) : ''}
          onChange={onChangeHandler}
          readOnly={!!isReadOnly}
          placeholder={helperText || undefined}
          bounds={boxRef}
          modules={modules}
        />
      )}
    </Box>
  );
}

RichTextHtmlField.propTypes = {
  field: P.shape({
    key: P.string.isRequired,
    title: P.string.isRequired,
    value: P.oneOfType([P.string]),
  }).isRequired,
  setChange: P.func.isRequired,
  isReadOnly: P.bool,
  label: P.string,
  helperText: P.string,
};

export default React.memo(RichTextHtmlField);
